import { z } from 'zod';
import type { Language } from '../enums.js';
import type { PatientAge } from '../patient-age.js';

/**
 * Carte « Anniversaires » du tableau de bord et courriel de vœux.
 *
 * Un courriel d'anniversaire n'est pas un soin : il ne porte **aucune donnée clinique**, et il ne
 * part pas vers un patient qui a refusé les courriels du cabinet. Faute d'un consentement propre
 * aux messages non médicaux, le refus retenu est celui des rappels de rendez-vous
 * (`acceptsAppointmentReminders`), le seul que le dossier porte.
 *
 * Un envoi par patient et par anniversaire : un second exige `resend: true`, après un
 * avertissement explicite à l'écran.
 */

export const BIRTHDAY_EMAIL_SUBJECT_MAX_LENGTH = 200;
export const BIRTHDAY_EMAIL_BODY_MAX_LENGTH = 4_000;

/** Pourquoi le courriel ne peut pas partir. `declined` : le patient refuse les courriels. */
export const birthdayEmailBlockers = ['no_email', 'declined'] as const;
export type BirthdayEmailBlocker = (typeof birthdayEmailBlockers)[number];

export interface BirthdayDto {
  patientId: string;
  firstName: string;
  lastName: string;
  /** Jour fêté, `YYYY-MM-DD` : le 1er mars d'une année commune pour un 29 février. */
  date: string;
  isToday: boolean;
  /** Âge atteint ce jour-là, selon `patientAgeAt`. */
  age: PatientAge | null;
  /** Langue du courriel : celle du patient, à défaut celle du cabinet. */
  language: Language;
  blocker: BirthdayEmailBlocker | null;
  /** Dernier envoi pour cet anniversaire-ci ; nul s'il n'est pas encore parti. */
  sentAt: string | null;
}

export interface BirthdaysDto {
  /** Jour civil du cabinet (Europe/Luxembourg) qui ouvre la fenêtre. */
  today: string;
  items: BirthdayDto[];
}

/** Message proposé dans le dialogue, variables déjà remplacées, dans la langue du patient. */
export interface BirthdayEmailDraftDto {
  birthday: BirthdayDto;
  subject: string;
  body: string;
}

export const birthdayPatientParamSchema = z.object({ patientId: z.uuid() }).strict();

export const birthdayEmailSendSchema = z
  .object({
    subject: z.string().trim().min(1).max(BIRTHDAY_EMAIL_SUBJECT_MAX_LENGTH),
    body: z.string().trim().min(1).max(BIRTHDAY_EMAIL_BODY_MAX_LENGTH),
    /** Vrai pour envoyer une seconde fois le même anniversaire, après l'avertissement. */
    resend: z.boolean().optional(),
  })
  .strict();
export type BirthdayEmailSendInput = z.infer<typeof birthdayEmailSendSchema>;

export interface BirthdayEmailSentDto {
  sentAt: string;
}

/** « Envoyer à tous ceux d'aujourd'hui » : le message par défaut, dans la langue de chacun. */
export const birthdayBulkSendSchema = z.object({}).strict();

export interface BirthdayBulkSendDto {
  sent: number;
  skipped: { noEmail: number; declined: number; alreadySent: number };
}
